'use client'

import { useAtom, useSetAtom } from 'jotai'
import { useLocale, useTranslations } from 'next-intl'
import { useTheme } from 'next-themes'
import { useState } from 'react'

import {
  SettingsCard,
  SettingsRow,
  SettingsRowGroup,
} from '@/components/dialog/settings/settings-card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { AUTO_HIDE_DAYS_OPTIONS } from '@/features/dashboard/lib/page-visibility'
import { useDefaultOrderAccounts } from '@/hooks/use-default-order-accounts'
import { type AppLocale, localeCookieName, localeLabels, locales } from '@/i18n/config'
import { applyPageTheme, type PageTint, readPageTint, writePageTint } from '@/lib/apply-page-theme'
import { localizeSeparators, SEPARATOR_STYLES, type SeparatorStyle } from '@/lib/number-format'
import {
  CHART_ENGINES,
  type ChartEngine,
  chartEngineAtom,
  hideBalancesAtom,
  layoutLockedAtom,
  numberFormatAtom,
  pageAutoHideDaysAtom,
  STREAM_INTERVAL_MODES,
  type StreamIntervalMode,
  streamIntervalModeAtom,
} from '@/state/storage'
import { tutorialHubOpenAtom } from '@/state/tutorial'

export default function SettingGeneral({
  onNavigateAway,
}: {
  /** Closes the surrounding Settings dialog, when there is one, before going elsewhere. */
  onNavigateAway?: () => void
} = {}) {
  const commonT = useTranslations('common')
  const tradeT = useTranslations('trade')
  const settingsT = useTranslations('settings')
  const [hideBalances, setHideBalances] = useAtom(hideBalancesAtom)
  const [layoutLocked, setLayoutLocked] = useAtom(layoutLockedAtom)
  const openTutorialHub = useSetAtom(tutorialHubOpenAtom)
  return (
    <SettingsCard>
      <SettingsRowGroup>
        <SettingsRow
          label={tradeT('theme')}
          description={settingsT('general.themeDescription')}
          control={<ThemeDropdown />}
        />
        <SettingsRow
          label={commonT('language')}
          description={settingsT('general.languageDescription')}
          control={<LanguageDropdown />}
        />
        <SettingsRow
          label={settingsT('numberFormat')}
          description={settingsT('general.numberFormatDescription')}
          control={<NumberFormatDropdown />}
        />
        <SettingsRow
          label={commonT('hideBalances')}
          description={settingsT('general.hideBalancesDescription')}
          control={
            <Switch
              checked={hideBalances}
              onCheckedChange={setHideBalances}
              aria-label={commonT('hideBalances')}
            />
          }
        />
        <SettingsRow
          label={settingsT('general.layoutLock')}
          description={settingsT('general.layoutLockDescription')}
          control={
            <Switch
              checked={layoutLocked}
              onCheckedChange={setLayoutLocked}
              aria-label={settingsT('general.layoutLock')}
            />
          }
        />
        <SettingsRow
          label={settingsT('general.chartEngine')}
          description={settingsT('general.chartEngineDescription')}
          control={<ChartEngineDropdown />}
        />
        <SettingsRow
          label={settingsT('general.streamInterval')}
          description={settingsT('general.streamIntervalDescription')}
          control={<StreamIntervalDropdown />}
        />
        <DefaultOrderAccountRow />
        <SettingsRow
          label={tradeT('pageTabs.autoHideLabel')}
          description={settingsT('general.autoHideDescription')}
          control={<AutoHideTabsDropdown />}
        />
        <SettingsRow
          label={settingsT('general.tutorials')}
          description={settingsT('general.tutorialsDescription')}
          control={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Out of the Settings dialog first: a tour started from the hub must not run
                // underneath it.
                onNavigateAway?.()
                openTutorialHub(true)
              }}
            >
              {settingsT('general.tutorialsShow')}
            </Button>
          }
        />
      </SettingsRowGroup>
    </SettingsCard>
  )
}

/** Shared by every General-tab picker so the controls line up in one column. */
const SETTINGS_SELECT_TRIGGER_CLASS = 'h-8 w-42 px-3 text-sm tracking-tight'

export const BROWSER_DEFAULT_LOCALE_VALUE = 'browser'

export function setLocaleCookie(locale: AppLocale | null) {
  if (locale) {
    document.cookie = `${localeCookieName}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`
  } else {
    document.cookie = `${localeCookieName}=; Path=/; Max-Age=0; SameSite=Lax`
  }
}

function LanguageDropdown() {
  const t = useTranslations('common')
  const locale = useLocale() as AppLocale

  const handleLocaleChange = (value: string) => {
    setLocaleCookie(value === BROWSER_DEFAULT_LOCALE_VALUE ? null : (value as AppLocale))
    window.location.reload()
  }

  return (
    <Select value={locale} onValueChange={handleLocaleChange}>
      <SelectTrigger className={SETTINGS_SELECT_TRIGGER_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="w-42">
        <SelectItem value={BROWSER_DEFAULT_LOCALE_VALUE} className="text-sm">
          {t('browserDefault')}
        </SelectItem>
        {locales.map((item) => (
          <SelectItem key={item} value={item} className="text-sm">
            {localeLabels[item]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export const THEME_VALUES = ['blue', 'dark', 'light'] as const
export type ThemeValue = (typeof THEME_VALUES)[number]

// Each picker value is a next-themes theme plus a tint: OrderX Blue (the default) is dark on
// the brand navy, plain Dark opts out of it, and Light has no tinted variant.
export const THEME_OPTIONS: Record<ThemeValue, { theme: 'dark' | 'light'; tint: PageTint }> = {
  blue: { theme: 'dark', tint: 'blue' },
  dark: { theme: 'dark', tint: 'ink' },
  light: { theme: 'light', tint: 'ink' },
}

export function toThemeValue(theme: string | undefined, tint: PageTint): ThemeValue {
  if (theme === 'light') return 'light'
  return tint === 'blue' ? 'blue' : 'dark'
}

export const ThemeDropdown = ({ className }: { className?: string }) => {
  const t = useTranslations('settings')
  const { theme, setTheme } = useTheme()
  const [tint, setTint] = useState<PageTint>(readPageTint)

  const value = toThemeValue(theme, tint)

  const handleThemeChange = (next: string) => {
    const option = THEME_OPTIONS[next as ThemeValue]
    if (!option) return
    writePageTint(option.tint)
    setTint(option.tint)
    applyPageTheme(option.theme, option.tint)
    setTheme(option.theme)
  }

  return (
    <Select value={value} onValueChange={handleThemeChange}>
      <SelectTrigger className={className ?? SETTINGS_SELECT_TRIGGER_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="w-42">
        {THEME_VALUES.map((value) => (
          <SelectItem key={value} value={value} className="text-sm">
            {t(value)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Same atom as each chart's ⋮ → Chart Settings → Engine. */
function ChartEngineDropdown() {
  const t = useTranslations('trade.chartSettings')
  const [engine, setEngine] = useAtom(chartEngineAtom)

  return (
    <Select value={engine} onValueChange={(v) => setEngine(v as ChartEngine)}>
      <SelectTrigger className={SETTINGS_SELECT_TRIGGER_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="w-42">
        {CHART_ENGINES.map((value) => (
          <SelectItem key={value} value={value} className="text-sm">
            {t(`engines.${value}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Market-data cadence on this device's sockets — see service/ws/stream-interval.ts. */
function StreamIntervalDropdown() {
  const t = useTranslations('settings.general.streamIntervalModes')
  const [mode, setMode] = useAtom(streamIntervalModeAtom)

  return (
    <Select value={mode} onValueChange={(v) => setMode(v as StreamIntervalMode)}>
      <SelectTrigger className={SETTINGS_SELECT_TRIGGER_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="w-42">
        {STREAM_INTERVAL_MODES.map((value) => (
          <SelectItem key={value} value={value} className="text-sm">
            {t(value)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const AUTO_ACCOUNT_VALUE = 'auto'

/** One picker per venue with order-capable accounts; hidden with none. Shared with the Order
 *  Form's settings menu and Cmd+K through useDefaultOrderAccounts. */
function DefaultOrderAccountRow() {
  const settingsT = useTranslations('settings')
  const orderFormT = useTranslations('orderForm')
  const { venues, setDefaultAccount } = useDefaultOrderAccounts()
  if (venues.length === 0) return null

  return (
    <SettingsRow
      label={settingsT('general.defaultOrderAccount')}
      description={settingsT('general.defaultOrderAccountDescription')}
      control={
        <div className="flex flex-col items-end gap-2">
          {venues.map((venue) => (
            <div key={venue.tradeVenue} className="flex items-center gap-2">
              <span className="text-foreground/60 text-sm">{venue.label}</span>
              <Select
                value={venue.defaultAccountId ?? AUTO_ACCOUNT_VALUE}
                onValueChange={(v) =>
                  setDefaultAccount(venue.tradeVenue, v === AUTO_ACCOUNT_VALUE ? undefined : v)
                }
              >
                <SelectTrigger
                  className={SETTINGS_SELECT_TRIGGER_CLASS}
                  aria-label={`${settingsT('general.defaultOrderAccount')} · ${venue.label}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end" className="w-42">
                  <SelectItem value={AUTO_ACCOUNT_VALUE} className="text-sm">
                    {orderFormT('defaultAccountAuto')}
                  </SelectItem>
                  {venue.accounts.map((account) => (
                    <SelectItem
                      key={account.accountId}
                      value={account.accountId}
                      className="text-sm"
                    >
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      }
    />
  )
}

function AutoHideTabsDropdown() {
  const tradeT = useTranslations('trade')
  const [autoHideDays, setAutoHideDays] = useAtom(pageAutoHideDaysAtom)

  return (
    <Select value={String(autoHideDays)} onValueChange={(v) => setAutoHideDays(Number(v))}>
      <SelectTrigger className={SETTINGS_SELECT_TRIGGER_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="w-42">
        {AUTO_HIDE_DAYS_OPTIONS.map((days) => (
          <SelectItem key={days} value={String(days)} className="text-sm">
            {days === 0
              ? tradeT('pageTabs.autoHideOff')
              : tradeT('pageTabs.autoHideDaysOption', { count: days })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const SEPARATOR_STYLE_VALUES = Object.keys(SEPARATOR_STYLES) as SeparatorStyle[]

/** Each option is labelled by its own sample, so no copy needs translating. */
function NumberFormatDropdown() {
  const [prefs, setPrefs] = useAtom(numberFormatAtom)

  return (
    <Select
      value={prefs.separators}
      onValueChange={(v) => setPrefs({ ...prefs, separators: v as SeparatorStyle })}
    >
      <SelectTrigger className={SETTINGS_SELECT_TRIGGER_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="w-42">
        {SEPARATOR_STYLE_VALUES.map((style) => (
          <SelectItem key={style} value={style} className="text-sm tabular-nums">
            {localizeSeparators('1,234,567.89', style)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
